import type { BankAccount } from '@/services/userData'
import { OnboardingDraftSchema, type OnboardingDraft } from '@shared/schemas/onboarding.schema'
import {
  draftWithoutBank,
  excludeBankFromOnboarding,
  type BankBearingDraft,
} from '@shared/utils/removeBank'

// In-progress onboarding state. Survives the full-page Google OAuth redirect
// (window.location.href in sheets.ts), which would otherwise wipe the context.
//
// Keyed by uid. logout() clears sessionStorage, but an account switch in another
// tab never reaches this one, so a shared key would hand the next user the
// previous one's selected accounts and nicknames.
export const stateKeyFor = (uid: string | undefined) => `v1_onboarding_state:${uid ?? 'anonymous'}`
const handoffKeyFor = (uid: string) => `${stateKeyFor(uid)}:plaid-handoff`

/** Completed choices are history, not a pending dashboard bank connection. */
export function restoreOnboardingSessionDraft(
  draft: Partial<OnboardingDraft> | null,
  isOnboarded: boolean,
  handoff: BankAccount[] | null
): Partial<OnboardingDraft> {
  return {
    ...(isOnboarded ? {} : draft),
    ...(handoff ? { pendingBankAccounts: handoff } : {}),
  }
}

export function persistOnboardingSession(
  uid: string | undefined,
  generation: number,
  draft: Partial<OnboardingDraft>,
  isOnboarded: boolean
): void {
  try {
    // Dashboard OAuth returns still need the current generation, but must not
    // resurrect completed choices as an unfinished onboarding draft.
    sessionStorage.setItem(
      stateKeyFor(uid),
      JSON.stringify({ ...(isOnboarded ? {} : draft), persistenceGeneration: generation })
    )
  } catch {
    // Storage unavailable: in-page flows still work.
  }
}

export function readBankHandoff(uid: string | undefined, generation: number): BankAccount[] | null {
  if (!uid) return null
  try {
    const value = JSON.parse(sessionStorage.getItem(handoffKeyFor(uid)) || 'null')
    if (value?.generation !== generation) return null
    return OnboardingDraftSchema.shape.pendingBankAccounts.parse(value.accounts)
  } catch {
    return null
  }
}

export function readBankHandoffRemovedBankIds(
  uid: string | undefined,
  generation: number
): string[] {
  if (!uid) return []
  try {
    const value = JSON.parse(sessionStorage.getItem(handoffKeyFor(uid)) || 'null')
    if (value?.generation !== generation || !Array.isArray(value.removedBankIds)) return []
    return value.removedBankIds.filter((id: unknown): id is string => typeof id === 'string')
  } catch {
    return []
  }
}

export function reconcileBankHandoffDraft<T extends BankBearingDraft>(
  draft: T,
  uid: string | undefined,
  generation: number
): T {
  const withoutRemovedBanks = readBankHandoffRemovedBankIds(uid, generation).reduce(
    (current, bankId) => draftWithoutBank(current, bankId),
    draft
  )
  return (withoutRemovedBanks.excludedBankIds ?? []).reduce(
    (current, bankId) => excludeBankFromOnboarding(current, bankId),
    withoutRemovedBanks
  )
}

export function acknowledgeBankHandoff(
  uid: string | undefined,
  generation: number,
  accounts: BankAccount[],
  /**
   * Banks skipped for this setup. A skip shortens the saved queue, so it can
   * never equal the handoff it came from; compared without them, it still can.
   * A newer OAuth result adds a bank that is not skipped, so it still does not
   * match and is not cleared by an older save.
   */
  excludedBankIds: readonly string[] = []
): void {
  if (!uid) return
  const handoff = readBankHandoff(uid, generation)
  const excluded = new Set(excludedBankIds)
  const pending = handoff?.filter((account) => !excluded.has(account.itemId))
  const normalized = OnboardingDraftSchema.shape.pendingBankAccounts.safeParse(accounts)
  if (
    !pending ||
    !normalized.success ||
    JSON.stringify(pending) !== JSON.stringify(normalized.data)
  )
    return
  try {
    sessionStorage.removeItem(handoffKeyFor(uid))
  } catch {
    /* A replay is harmless. */
  }
}

/**
 * Retire any Plaid handoff belonging to a completed onboarding run.
 *
 * Draft saves use exact account matching because a newer OAuth result must not
 * be acknowledged by an older save. Completion is different: the per-bank queue
 * may have advanced since the handoff was staged, so its last snapshot is not
 * expected to contain the same accounts. The generation remains the boundary
 * that prevents completion from clearing a handoff created after a reset.
 */
export function clearBankHandoff(uid: string | undefined, generation: number): void {
  if (!uid) return
  try {
    const key = handoffKeyFor(uid)
    const value = JSON.parse(sessionStorage.getItem(key) || 'null')
    if (value?.generation === generation) sessionStorage.removeItem(key)
  } catch {
    // Storage unavailable or malformed: completion itself still succeeded.
  }
}

/**
 * Put a freshly connected bank's accounts into the persisted draft, from outside
 * the provider.
 *
 * The Plaid OAuth callback lives at /oauth/callback, a sibling of /v1/* in the
 * router, so it renders outside this provider and cannot call
 * setPendingBankAccounts. Without this the bank is connected correctly and then
 * the user lands on the card step with nothing to choose from, because the state
 * the in-page path sets in memory never survives a full-page redirect.
 *
 * Writes through the same key the provider reads on mount, so the draft is
 * restored with the accounts already in it. The key and the shape stay owned
 * here rather than being reconstructed by the caller.
 */
export function stagePendingBankAccounts(
  uid: string | undefined,
  accounts: BankAccount[],
  removedBankIds: string[] = [],
  mergeWithExisting = false,
  persistenceGeneration?: number
): void {
  if (!uid) return
  // A draft that will not parse is treated as an empty one, not as a reason to
  // skip the write. Suppressing both together meant a corrupted draft threw away
  // the accounts of a bank that had just been connected: the provider clears the
  // bad entry and starts fresh, so the card step opened with nothing to choose
  // and sent the user back to connect a bank they already had.
  let existing: Record<string, unknown> = {}
  try {
    const raw = sessionStorage.getItem(stateKeyFor(uid))
    const parsed: unknown = raw ? JSON.parse(raw) : null
    // JSON.parse('null') or an array is valid JSON but not a draft; reading
    // fields off it below would throw outside this try.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      existing = parsed as Record<string, unknown>
    }
  } catch {
    // Unreadable or unparseable — the accounts still have to land.
  }

  const generation =
    persistenceGeneration ??
    (typeof existing.persistenceGeneration === 'number' ? existing.persistenceGeneration : 0)

  const excludedBankIds = Array.isArray(existing.excludedBankIds)
    ? existing.excludedBankIds.filter((id): id is string => typeof id === 'string')
    : []
  const excluded = new Set(excludedBankIds)
  let stagedAccounts = accounts.filter((account) => !excluded.has(account.itemId))
  if (mergeWithExisting) {
    const previous = OnboardingDraftSchema.shape.pendingBankAccounts.safeParse(
      existing.pendingBankAccounts
    )
    if (previous.success) {
      const merged = new Map<string, BankAccount>()
      for (const account of [...previous.data, ...stagedAccounts]) {
        merged.set(`${account.itemId}\u0000${account.accountId}`, account)
      }
      stagedAccounts = [...merged.values()].filter((account) => !excluded.has(account.itemId))
    }
  }

  try {
    // This result is newer than the server draft. Keep it until that draft has
    // acknowledged it, but never carry it across an explicit onboarding reset.
    sessionStorage.setItem(
      handoffKeyFor(uid),
      JSON.stringify({
        generation,
        accounts: stagedAccounts,
        removedBankIds,
      })
    )
    sessionStorage.setItem(
      stateKeyFor(uid),
      JSON.stringify({
        ...existing,
        persistenceGeneration: generation,
        pendingBankAccounts: stagedAccounts,
      })
    )
  } catch {
    // Storage genuinely unavailable (private mode, quota). Nothing more can be
    // done here, and failing a connection that already succeeded would be worse.
  }
}
